import { useEffect } from "react";
import { useSelector } from "react-redux";

import { useGetAttributeValues } from "@/api/dataset/useDatasetAPI";
import { Timezone } from "@/constants/dateTime";
import {
  getVisibleSelectedCount,
  isNotAdminAreaLevel
} from "@/helpers/datasetAttributeHelper";
import { formatDecimalNumber } from "@/helpers/numberHelpers";
import type { RootState } from "@/store";
import {
  AttributeOption,
  DatasetFilter,
  EFilterOptionType,
  FilterOption
} from "@/types/dataset";

import dayjs from "dayjs";

import SavedFilterCard from "./SavedFilterCard";

interface SavedFilterItemProps {
  filter: DatasetFilter;
  filterOptions: FilterOption[];
  datasetId?: string;
  timezone?: Timezone;
  allSavedFilters: DatasetFilter[];
  onEdit?: (filter: DatasetFilter) => void;
  onDelete?: (filter: DatasetFilter) => void;
  onUpdateFilter?: (updatedFilter: DatasetFilter) => void;
}

/**
 * Builds an AttributeOption from a DatasetFilter for use with useGetAttributeValues
 */
const buildAttributeOptionFromFilter = (
  filter: DatasetFilter
): AttributeOption => ({
  id: filter.attributeId || filter.filterId,
  label: filter.filterLabel || "",
  value: filter.attributeId || filter.filterId,
  attribute_code: filter.attributeCode,
  data_type: filter.data_type,
  type: filter.filterType,
  paired_key_attribute_id: filter.paired_key_attribute_id ?? null
});

/**
 * Renders the content for date/time/min-max filters
 */
const renderStaticFilterContent = (filter: DatasetFilter) => {
  if (filter.dateTimeRange) {
    const { from, to } = filter.dateTimeRange;
    return (
      <div className="text-xs text-txtPlaceholder font-normal">
        <div>
          From{" "}
          <span className="text-txtHeading font-semibold">
            {from.date && from.time
              ? dayjs(`${from.date} ${from.time}`).format("D MMM YYYY, HH:mm")
              : ""}
          </span>
          {","}
        </div>
        <div>
          Until{" "}
          <span className="text-txtHeading font-semibold">
            {to.date && to.time
              ? dayjs(`${to.date} ${to.time}`).format("D MMM YYYY, HH:mm")
              : ""}
          </span>
        </div>
      </div>
    );
  }

  if (filter.dateRange) {
    const { from, to } = filter.dateRange;
    return (
      <div className="text-xs text-txtPlaceholder font-normal">
        <div>
          From{" "}
          <span className="text-txtHeading font-semibold">
            {from ? dayjs(`${from}`).format("D MMM YYYY") : ""}
          </span>
          {","}
        </div>
        <div>
          Until{" "}
          <span className="text-txtHeading font-semibold">
            {to ? dayjs(`${to}`).format("D MMM YYYY") : ""}
          </span>
        </div>
      </div>
    );
  }

  if (filter.timeRange) {
    const { from, to } = filter.timeRange;
    return (
      <div className="text-xs text-txtPlaceholder font-normal">
        <div>
          From{" "}
          <span className="text-txtHeading font-semibold">{from ?? ""}</span>,
          Until{" "}
          <span className="text-txtHeading font-semibold">{to ?? ""}</span>
        </div>
      </div>
    );
  }

  if (filter.minMaxRange) {
    return (
      <div className="text-xs text-txtBody">
        <span>
          Min.{" "}
          <span className="text-txtHeading font-semibold">
            {formatDecimalNumber(filter.minMaxRange?.min)}
          </span>
          , Max.{" "}
          <span className="text-txtHeading font-semibold">
            {formatDecimalNumber(filter.minMaxRange?.max)}
          </span>
        </span>
      </div>
    );
  }

  return null;
};

/**
 * Renders the content for multiple selection filters with visible count
 */
const renderMultipleFilterContent = (
  filter: DatasetFilter,
  attributeValues: FilterOption[],
  isLoading: boolean
) => {
  // Show loading state
  if (isLoading) {
    return (
      <div className="text-xs text-txtPlaceholder">Loading options...</div>
    );
  }

  // Show count when data is available
  if (attributeValues && attributeValues.length > 0) {
    const visibleCount = getVisibleSelectedCount(
      filter.selectedOptions || [],
      attributeValues
    );
    return (
      <div className="text-xs text-txtPlaceholder">
        {visibleCount} Options Selected
      </div>
    );
  }

  // Show generic text when no data or error
  return <div className="text-xs text-txtPlaceholder">Options Selected</div>;
};

const SavedFilterItem = ({
  filter,
  filterOptions,
  datasetId,
  timezone,
  allSavedFilters,
  onEdit,
  onDelete,
  onUpdateFilter
}: SavedFilterItemProps) => {
  // Build the AttributeOption for this filter
  const selectedFilter = buildAttributeOptionFromFilter(filter);
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );

  // Get other filters for context (exclude current filter)
  const otherFilters = allSavedFilters.filter(
    (f) => f.filterId !== filter.filterId
  );

  const isAdminAreaFilter = !isNotAdminAreaLevel(filter.attributeCode);

  // Fetch attribute values for this filter
  const { data: attributeValues = [], isLoading } = useGetAttributeValues({
    savedFilters: otherFilters,
    timezone,
    datasetId,
    selectedFilter,
    selectedEditFilter: null,
    isMultipleSelection:
      !isAdminAreaFilter && filter.filterType === EFilterOptionType.MULTIPLE,
    isEditMode: false,
    showAnalysisResultPopup: showAnalysisResultPopup, // condition from show analysis result or config
    latestAnalysisId: latestAnalysisId // pass latestAnalysisId
  });

  // Auto-update stale selectedOptions when attributeValues change
  useEffect(() => {
    if (
      !attributeValues.length ||
      isLoading ||
      !onUpdateFilter ||
      isAdminAreaFilter ||
      filter.filterType !== EFilterOptionType.MULTIPLE
    )
      return;

    const validOptions = (filter.selectedOptions || []).filter((opt) =>
      attributeValues.some((av) => av.value === opt)
    );

    // Only update if options actually changed
    if (validOptions.length !== (filter.selectedOptions || []).length) {
      onUpdateFilter({
        ...filter,
        selectedOptions: validOptions
      });
    }
  }, [attributeValues, isLoading, filter, onUpdateFilter, isAdminAreaFilter]);

  // Determine the title
  const title =
    filter.filterLabel ||
    filterOptions.find((o) => o.id === filter.filterId)?.label;

  // Render appropriate content based on filter type
  const content =
    filter.filterType === EFilterOptionType.MULTIPLE
      ? renderMultipleFilterContent(filter, attributeValues, isLoading)
      : renderStaticFilterContent(filter);

  // Skip admin area level filters
  if (!isNotAdminAreaLevel(filter.attributeCode)) {
    return null;
  }

  return (
    <SavedFilterCard
      filter={filter}
      title={title}
      content={content}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
};

export default SavedFilterItem;
